import PushSheet from '@/components/PushSheet';
import EmptyState from '@/components/EmptyState';
import { useInbox, useMarkAllRead, useMarkRead } from '@/features/notifications/hooks/useInbox';
import { notificationDeepLink } from '@/features/notifications/lib/deepLink';
import type { Notification } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Preloader,
  Toolbar,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { DateTime } from 'luxon';
import { useRef } from 'react';

function relativeTime(iso: string): string {
  return DateTime.fromISO(iso).toRelative() ?? '';
}

function InboxContent({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = useInbox(false);
  const markRead = useMarkRead();

  const open = (n: Notification) => {
    if (!n.readAt) markRead.mutate(n.id);
    const link = notificationDeepLink(n.data);
    onClose();
    if (link) f7.views.main.router.navigate(link);
  };

  const items = data?.items ?? [];
  const today = items.filter((n) => DateTime.fromISO(n.createdAt).hasSame(DateTime.now(), 'day'));
  const earlier = items.filter(
    (n) => !DateTime.fromISO(n.createdAt).hasSame(DateTime.now(), 'day'),
  );

  const renderGroup = (title: string, rows: Notification[]) =>
    rows.length === 0 ? null : (
      <>
        <BlockTitle>{title}</BlockTitle>
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
          {rows.map((n) => (
            <ListItem
              key={n.id}
              link="#"
              onClick={() => open(n)}
              title={n.title}
              text={n.body}
              after={relativeTime(n.createdAt)}
            >
              <div
                slot="media"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: n.readAt ? 'transparent' : 'var(--f7-theme-color,#007aff)',
                  marginTop: 6,
                }}
              />
            </ListItem>
          ))}
        </List>
      </>
    );

  return (
    <div className="page-content">
      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title="Couldn’t load notifications"
          description={(error as Error).message}
        />
      )}
      {data && items.length === 0 && (
        <EmptyState
          icon="bell_slash"
          iconColor="#34c759"
          title="All caught up"
          description="No notifications."
        />
      )}
      {renderGroup('Today', today)}
      {renderGroup('Earlier', earlier)}
    </div>
  );
}

export function InboxSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const markAll = useMarkAllRead();
  // Guard a rapid double-tap (idempotent, but avoids a redundant request).
  const submittingRef = useRef(false);
  const markAllRead = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    markAll.mutate(undefined, {
      onSettled: () => {
        submittingRef.current = false;
      },
    });
  };
  return (
    <PushSheet
      open={open}
      onClose={onClose}
      className="sheet-side-panel"
      style={{ height: '80vh' }}
    >
      <Toolbar>
        <div className="left">
          <F7Link sheetClose>Close</F7Link>
        </div>
        <div className="title">Notifications</div>
        <div className="right">
          <F7Link onClick={markAllRead}>Mark all read</F7Link>
        </div>
      </Toolbar>
      <InboxContent onClose={onClose} />
    </PushSheet>
  );
}
